import React, { useMemo } from "react";
import Table, { Column } from "@/components/Feature/Table";
import { MgtConsumerBackgroundAnalyzeDto } from "@/api/swagger";

const consumerAgeLevelLabels: Record<
  MgtConsumerBackgroundAnalyzeDto["consumerAgeLevel"] | "TOTAL",
  string
> = {
  BELOW16: "16歲以下",
  BELOW20: "17-20歲",
  BELOW25: "21-25歲",
  BELOW30: "26-30歲",
  BELOW35: "31-35歲",
  BELOW40: "36-40歲",
  BELOW45: "41-45歲",
  BELOW50: "46-50歲",
  BELOW55: "51-55歲",
  BELOW60: "56-60歲",
  BELOW65: "61-65歲",
  BELOW70: "66-70歲",
  OVER50: "51歲以上",
  OVER70: "71歲以上",
  UNKNOWN: "未知",
  TOTAL: "合計(100%)",
};

const consumerAgeLevels = Object.keys(consumerAgeLevelLabels) as Array<
  keyof typeof consumerAgeLevelLabels
>;

interface ConsumerAgeData {
  type: keyof typeof consumerAgeLevelLabels;
  MALE: number;
  FEMALE: number;
  OTHER: number;
  totalCount: number;
}

const ConsumerAgeDistributionAnalysis: React.FC<{
  data: Array<MgtConsumerBackgroundAnalyzeDto>;
}> = ({ data }) => {
  const value = useMemo(() => {
    const ageMaps = new Map<
      keyof typeof consumerAgeLevelLabels,
      ConsumerAgeData
    >();

    const totalCount = data.reduce((prev, curr) => {
      return prev + curr.cnt;
    }, 0);

    consumerAgeLevels.map((level) =>
      ageMaps.set(level, {
        type: level,
        MALE: 0,
        FEMALE: 0,
        OTHER: 0,
        totalCount,
      })
    );

    data.forEach(({ consumerAgeLevel, gender, cnt }) => {
      const data = ageMaps.get(
        consumerAgeLevel as keyof typeof consumerAgeLevelLabels
      );
      const totalData = ageMaps.get("TOTAL")!;
      if (!data) return;
      data[gender] = cnt;
      totalData[gender] = totalData[gender] + cnt;
      ageMaps.set(
        consumerAgeLevel as keyof typeof consumerAgeLevelLabels,
        data
      );
    });

    const unknownData = ageMaps.get("UNKNOWN")!;
    if (
      unknownData.MALE === 0 &&
      unknownData.FEMALE === 0 &&
      unknownData.OTHER === 0
    )
      ageMaps.delete("UNKNOWN");

    return [...ageMaps.values()];
  }, [data]);

  const columns = useMemo<Array<Column<ConsumerAgeData>>>(() => {
    return [
      {
        header: "年齡",
        flex: 5,
        accessor: "type",
        formatter: ({ type }) => consumerAgeLevelLabels[type],
      },
      {
        header: "女性",
        accessor: "FEMALE",
        formatter: ({ FEMALE, totalCount }) =>
          `${((FEMALE / totalCount) * 100).toFixed(1)}%`,
      },
      {
        header: "男性",
        accessor: "MALE",
        formatter: ({ MALE, totalCount }) =>
          `${((MALE / totalCount) * 100).toFixed(1)}%`,
      },
      {
        header: "其他",
        accessor: "OTHER",
        formatter: ({ OTHER, totalCount }) =>
          `${((OTHER / totalCount) * 100).toFixed(1)}%`,
      },
    ];
  }, []);

  return <Table data={value} columns={columns} />;
};

export default ConsumerAgeDistributionAnalysis;
